/**
 * CommonTabs.js - Tab layout aligned with admin4.0 CommonTabs/CommonSubTab.
 * Uses react-router-dom v6. Supports main tabs only (subMenu.length === 0) and main+sub tabs.
 * Uses pathname as source of truth and array index for MUI Tabs value to avoid duplicate-id issues.
 */
import React, { useMemo, useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Tabs, Tab, IconButton, Tooltip, Box, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { usePermissions } from "../../Utility/PermissionContext";
import { canViewTab } from "../../Utility/Services/RoleAccessService";

function findSelectionByPath(pathname, list) {
  if (!list?.length) return { item: null, sub: null, mainIndex: 0, subIndex: 0 };
  for (let i = 0; i < list.length; i++) {
    const item = list[i];
    if (item.path === pathname) return { item, sub: null, mainIndex: i, subIndex: 0 };
    const subMenu = item.subMenu || [];
    for (let j = 0; j < subMenu.length; j++) {
      if (subMenu[j].path === pathname) return { item, sub: subMenu[j], mainIndex: i, subIndex: j };
    }
  }
  return { item: null, sub: null, mainIndex: 0, subIndex: 0 };
}

function findSelectionByTab(selectedTab, list) {
  if (!list?.length) return { item: list[0] || null, sub: null, mainIndex: 0, subIndex: 0 };
  if (!selectedTab?.id) return { item: list[0], sub: (list[0].subMenu && list[0].subMenu[0]) || null, mainIndex: 0, subIndex: 0 };
  const mainIdx = list.findIndex((it) => it.id === selectedTab.id);
  if (mainIdx >= 0) {
    const item = list[mainIdx];
    const sub = (item.subMenu && item.subMenu[0]) || null;
    const subIndex = sub ? 0 : 0;
    return { item, sub, mainIndex: mainIdx, subIndex };
  }
  for (let i = 0; i < list.length; i++) {
    const subMenu = list[i].subMenu || [];
    const subIdx = subMenu.findIndex((sm) => sm.id === selectedTab.id);
    if (subIdx >= 0) return { item: list[i], sub: subMenu[subIdx], mainIndex: i, subIndex: subIdx };
  }
  return { item: list[0], sub: (list[0].subMenu && list[0].subMenu[0]) || null, mainIndex: 0, subIndex: 0 };
}

function CommonTabsCore({ List = [], selectedTab, BackToHome, role, subscriptionData }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;

  const filteredList = useMemo(() => {
    if (!List || !List.length) return [];
    return List.filter((tab) => canViewTab(tab.menuName));
  }, [List]);

  const selectionFromPath = useMemo(
    () => findSelectionByPath(pathname, filteredList),
    [pathname, filteredList]
  );

  const selectionFromProp = useMemo(
    () => findSelectionByTab(selectedTab, filteredList),
    [selectedTab, filteredList]
  );

  const hasMatchByPath = selectionFromPath.item != null || selectionFromPath.sub != null;
  const selection = hasMatchByPath ? selectionFromPath : selectionFromProp;
  const { item: selectedItem, sub: selectedSubItem, mainIndex, subIndex } = selection;

  const [mainTabIndex, setMainTabIndex] = useState(mainIndex);
  const [subTabIndex, setSubTabIndex] = useState(subIndex);

  useEffect(() => {
    setMainTabIndex(selection.mainIndex);
    setSubTabIndex(selection.subIndex);
  }, [selection.mainIndex, selection.subIndex]);

  const handleMainChange = useCallback(
    (e, newIndex) => {
      const item = filteredList[newIndex];
      if (!item) return;
      setMainTabIndex(newIndex);
      const firstSub = (item.subMenu && item.subMenu[0]) || null;
      setSubTabIndex(firstSub ? 0 : 0);
      if (item.path) navigate(item.path);
    },
    [filteredList, navigate]
  );

  const handleSubChange = useCallback(
    (e, newSubIndex) => {
      const item = filteredList[mainTabIndex];
      const subMenu = (item && item.subMenu) || [];
      const sub = subMenu[newSubIndex];
      if (sub) {
        setSubTabIndex(newSubIndex);
        if (sub.path) navigate(sub.path);
      }
    },
    [filteredList, mainTabIndex, navigate]
  );

  const handleBack = useCallback(() => {
    if (BackToHome) navigate(BackToHome);
  }, [BackToHome, navigate]);

  const renderMainComponent = useCallback(
    (data) => {
      if (!data) return <Typography color="text.secondary">No screen selected.</Typography>;
      return data.component ?? null;
    },
    []
  );

  if (!filteredList.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">No tabs available.</Typography>
      </Box>
    );
  }

  const currentItem = filteredList[mainTabIndex] ?? filteredList[0];
  const hasSubMenu = currentItem?.subMenu?.length > 0;
  const subMenu = currentItem?.subMenu ?? [];
  const currentSub = subMenu[subTabIndex] ?? subMenu[0];
  const componentToRender = hasSubMenu && currentSub ? currentSub : currentItem;

  return (
    <div>
      <Box
        className="eMed_tabWrapper"
        display="flex"
        flexDirection="row"
        alignItems="center"
        flexWrap="nowrap"
        sx={{ gap: 1 }}
      >
        {BackToHome && (
          <Tooltip title="Back" placement="top" arrow>
            <IconButton onClick={handleBack} color="primary" size="small" aria-label="Back">
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tabs
          value={mainTabIndex}
          onChange={handleMainChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: "2.5vw", flex: 1, minWidth: 0 }}
        >
          {filteredList.map((data, index) => (
            <Tab
              key={data.path || data.id || index}
              value={index}
              label={data.menuName}
              sx={{ textTransform: "capitalize", minHeight: "2vw", fontWeight: 600, fontSize: "0.85dvw" }}
            />
          ))}
        </Tabs>
      </Box>

      {currentItem && (
        <>
          {!hasSubMenu ? (
            <div className="eMed_MainComponentDiv" style={{ paddingTop: 8 }}>
              {renderMainComponent(currentItem)}
            </div>
          ) : (
            <div>
              {currentSub != null && (
                <>
                  <Box display="flex" flexDirection="row" alignItems="center" sx={{ mb: 1 }}>
                    <Tabs
                      value={subTabIndex}
                      onChange={handleSubChange}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{ minHeight: "2vw" }}
                    >
                      {subMenu.map((sub, idx) => (
                        <Tab
                          key={sub.path || sub.id || idx}
                          value={idx}
                          label={sub.menuName}
                          sx={{ textTransform: "capitalize", minHeight: "2vw", fontWeight: 600, fontSize: "0.85dvw" }}
                        />
                      ))}
                    </Tabs>
                  </Box>
                  <div className="eMed_SubComponentDiv">
                    {renderMainComponent(currentSub)}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function CommonTabs(props) {
  let role = null;
  let subscriptionData = null;
  try {
    const permissions = usePermissions();
    role = permissions?.role ?? null;
    subscriptionData = permissions?.subscriptionData ?? null;
  } catch {
    // outside PermissionsProvider
  }
  return (
    <CommonTabsCore
      {...props}
      role={role}
      subscriptionData={subscriptionData}
    />
  );
}
